/**
 * Lazy loading and deferred loading for video elements.
 * Handles both scroll-triggered autoplay and click-to-play behaviors.
 */
export const initLazyVideos = () => {
	// 1. Scroll-triggered Autoplay (for mockups, etc.)
	const lazyVideoObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const video = entry.target as HTMLVideoElement;
					const sources = video.querySelectorAll('source');

					sources.forEach((source) => {
						const src = source.dataset.src;
						if (src) {
							source.src = src;
							source.removeAttribute('data-src');
						}
					});

					if (sources.length > 0) {
						video.load();
						// Attempt to play, but catch errors (e.g. user hasn't interacted with page yet)
						video.play().catch(() => {
							// console.log('Autoplay was prevented. Waiting for user interaction.');
						});
					}

					lazyVideoObserver.unobserve(video);
				}
			});
		},
		{ threshold: 0.1, rootMargin: '200px' },
	);

	document.querySelectorAll('video.js-lazy-video').forEach((video) => {
		lazyVideoObserver.observe(video);
	});

	// 2. Click-to-play Deferred Videos (e.g. Manifesto)
	const videoTriggers = document.querySelectorAll('.js-video-trigger');

	videoTriggers.forEach((trigger) => {
		trigger.addEventListener('click', (e) => {
			e.preventDefault();
			const section = trigger.closest('.js-video-section');
			if (!section) return;

			const container = section.querySelector(
				'.js-video-container',
			) as HTMLElement;
			if (!container) return;

			const videoUrl = container.dataset.videoUrl;
			if (!videoUrl) return;

			// Create video element if it doesn't exist
			if (!container.querySelector('video')) {
				const video = document.createElement('video');
				video.src = videoUrl;
				video.controls = true;
				video.autoplay = true;
				video.playsInline = true;
				video.className = 'absolute inset-0 w-full h-full object-cover';
				container.appendChild(video);
			}

			// Show video container
			container.classList.remove('opacity-0', 'pointer-events-none');
			container.classList.add('z-30');
		});
	});
};
